import { createChecking } from 'app/api/protocole-response/protocole-response';
import { FileInfo } from 'app/ui/components/multi-file-uploader/MultiFileUploader';
import axios from 'axios';

import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { AppNS } from '../';
import {
  apiAddReportDocument,
  apiDeleteReportDocument,
  apiGetReport,
  apiSaveReportChecklist,
  apiSaveReportInspector,
  apiSaveReportNotes,
  apiSaveReportQuestionnaire,
  apiSaveReportTime,
  apiSubmitReport,
  apiUpdateReportInspection,
  apiUpdateReportTotalCost,
} from '../api/agency-reports/agency-reports';
import { checkReportValid, setEventInspectionFileStatus } from './agency-reports-validation';
import { AppThunk } from './index';

export interface AgencyReportsState {
  currentReport: AppNS.JobEventReport | null;
  isReportLoading: boolean;
  isReportLoadingError: boolean;
  isDeleteEventLoading: boolean;
  isDeleteEventError: boolean;
  isReportStateLoadingError: boolean;
  isReportStateLoading: boolean;

  isReportValid: boolean;
  isReportValidChecked: boolean;
  reportValidErrors: Array<string>;
  isRequestLoading: boolean;
  isRequestLoadingError: boolean;

  isCancellingJob: boolean;
  isCancellingJobError: boolean;

  detailsActionDone: boolean;

  isTimeLoading: boolean;
  isTimeLoadingError: string[];
  timeValidationError: boolean;
  isTimeSaved: boolean;

  isNotesLoading: boolean;
  isNotesLoadingError: string[];
  isNotesSaved: boolean;

  isInspectionQuestionnaireLoading: boolean;
  isInspectionQuestionnaireLoadingError: string[];
  isInspectionQuestionnaireSaved: boolean;

  isInspectionChecklistLoading: boolean;
  isInspectionChecklistLoadingError: string[];
  isInspectionChecklistSaved: boolean;

  isListItemLoading: boolean;
  isListItemLoadingError: boolean;

  isReportFinalizing: boolean;
  isReportFinalizingError: AppNS.Params | null;

  isDetailsPageTouched: boolean;

  isSavingInspector: boolean;
  isSavingInspectorError: boolean;
  isInspectorSaved: boolean;

  isInspectionUpdateLoading: boolean;
  updatingInspectionId: string;
  isInspectionUpdateLoadingError: string[];
  isInspectionUpdateSaved: boolean;

  isReportCostUpdating: boolean;
  isReportCostUpdated: boolean;
  isReportCostUpdatingError: AppNS.Params | null;
  checkingInError: AppNS.Params | null;
  isCheckingIn: boolean;
  checkingType: 'check_in' | 'check_out' | '';
}

const initialState: AgencyReportsState = {
  currentReport: null,
  isReportLoading: false,
  isReportLoadingError: false,
  isDeleteEventLoading: false,
  isDeleteEventError: false,
  isReportStateLoadingError: false,
  isReportStateLoading: false,
  isReportValid: false,
  isReportValidChecked: false,
  reportValidErrors: [],
  isRequestLoading: false,
  isRequestLoadingError: false,
  isCancellingJob: false,
  isCancellingJobError: false,
  detailsActionDone: false,
  isTimeLoading: false,
  isTimeLoadingError: [],
  timeValidationError: false,
  isTimeSaved: false,
  isNotesLoading: false,
  isNotesLoadingError: [],
  isNotesSaved: false,
  isInspectionQuestionnaireLoading: false,
  isInspectionQuestionnaireLoadingError: [],
  isInspectionQuestionnaireSaved: false,
  isInspectionChecklistLoading: false,
  isInspectionChecklistLoadingError: [],
  isInspectionChecklistSaved: false,
  isInspectionUpdateLoading: false,
  isInspectionUpdateLoadingError: [],
  updatingInspectionId: '',
  isInspectionUpdateSaved: false,
  isListItemLoading: false,
  isListItemLoadingError: false,
  isReportFinalizing: false,
  isReportFinalizingError: null,
  isDetailsPageTouched: false,
  isSavingInspector: false,
  isSavingInspectorError: false,
  isInspectorSaved: false,
  isReportCostUpdating: false,
  isReportCostUpdated: false,
  isReportCostUpdatingError: null,
  isCheckingIn: false,
  checkingInError: null,
  checkingType: '',
};

const agencyReports = createSlice({
  name: 'agencyReports',
  initialState,
  reducers: {
    getReportStart(state: AgencyReportsState) {
      state.currentReport = null;
      state.isReportLoading = true;
      state.detailsActionDone = false;
      state.isReportLoadingError = false;
      state.isNotesSaved = false;
      state.isReportCostUpdated = false;
      state.isReportCostUpdating = false;
      state.isInspectionQuestionnaireSaved = false;
    },
    getCurrentReportSuccess(state: AgencyReportsState, action: PayloadAction<AppNS.JobEventReport>) {
      state.isReportLoadingError = false;
      state.isReportLoading = false;
      state.currentReport = action.payload;
      checkReportValid(state);
    },
    getReportFailed(state: AgencyReportsState) {
      state.isReportLoadingError = true;
      state.isReportLoading = false;
    },
    setReportStateStart(state: AgencyReportsState) {
      state.isReportStateLoading = true;
    },
    setReportStateSuccess(state: AgencyReportsState, action: PayloadAction<AppNS.JobReportState>) {
      state.isReportStateLoading = false;
      if (state.currentReport) {
        state.currentReport.state = action.payload;
      }
    },
    setReportStateFailed(state: AgencyReportsState) {
      state.isReportStateLoadingError = true;
      state.isReportStateLoading = false;
    },
    setIsReportValidChecked(state: AgencyReportsState) {
      state.isReportValidChecked = true;
    },
    setDetailsActionDone(state: AgencyReportsState, action: PayloadAction<boolean>) {
      state.detailsActionDone = action.payload;
    },
    setDetailsPageTouched(state: AgencyReportsState, action: PayloadAction<boolean>) {
      state.isDetailsPageTouched = action.payload;
    },
    setInspectionState(
      state: AgencyReportsState,
      action: PayloadAction<Pick<AppNS.Inspection, 'id' | 'status' | 'notes'>>
    ) {
      if (state.currentReport) {
        state.currentReport.eventInspections = state.currentReport.eventInspections.map((inspection) => {
          if (inspection.id === action.payload.id) {
            return {
              ...inspection,
              status: action.payload.status,
              notes: action.payload.notes,
            };
          }
          return inspection;
        });
        checkReportValid(state);
      }
    },
    saveInspectorStart(state: AgencyReportsState) {
      state.isInspectorSaved = false;
      state.isSavingInspector = true;
      state.isSavingInspectorError = false;
    },
    saveInspectorFailed(state: AgencyReportsState) {
      state.isSavingInspector = false;
      state.isSavingInspectorError = true;
    },
    saveInspectorSuccess(state: AgencyReportsState, action: PayloadAction<AppNS.Params>) {
      agencyReports.caseReducers.setInspector(state, action);
      state.isSavingInspector = false;
      state.isInspectorSaved = true;
    },
    setInspector(state: AgencyReportsState, action: PayloadAction<AppNS.Params>) {
      if (state.currentReport) {
        state.currentReport = { ...state.currentReport, ...action.payload };
      }
    },
    setNotesStart(state: AgencyReportsState) {
      if (state.currentReport) {
        state.isNotesLoadingError = [];
        state.isNotesLoading = true;
        state.isNotesSaved = false;
      }
    },
    setNotesFailed(state: AgencyReportsState, action: PayloadAction<string[]>) {
      if (state.currentReport) {
        state.isNotesLoading = false;
        state.isNotesLoadingError = action.payload;
        state.isNotesSaved = false;
      }
    },
    setNotesSuccess(state: AgencyReportsState, action: PayloadAction<string>) {
      if (state.currentReport) {
        state.isNotesLoading = false;
        state.isNotesLoadingError = [];
        state.currentReport.notes = action.payload;
        state.isNotesSaved = true;
      }
    },

    setInspectionUpdateStart(state: AgencyReportsState, action: PayloadAction<string>) {
      if (state.currentReport) {
        state.updatingInspectionId = action.payload;
        state.isInspectionUpdateLoadingError = [];
        state.isInspectionUpdateLoading = true;
        state.isInspectionUpdateSaved = false;
      }
    },
    setInspectionUpdateFailed(state: AgencyReportsState, action: PayloadAction<string[]>) {
      if (state.currentReport) {
        state.isInspectionUpdateLoading = false;
        state.isInspectionUpdateLoadingError = action.payload;
        state.isInspectionUpdateSaved = false;
      }
    },
    setInspectionUpdateSuccess(
      state: AgencyReportsState,
      action: PayloadAction<{
        id: string;
        notes: string;
        state: AppNS.InspectionStatus;
        failedCodes: string[];
        customFailedCodes: string;
      }>
    ) {
      if (state.currentReport) {
        state.isInspectionUpdateLoading = false;
        state.isInspectionUpdateLoadingError = [];

        state.currentReport = {
          ...state.currentReport,
          eventInspections: state.currentReport.eventInspections.map((event) => {
            if (event.id === action.payload.id) {
              return {
                ...event,
                notes: action.payload.notes,
                status: action.payload.state,
                failedCodes: action.payload.failedCodes,
                customFailedCodes: action.payload.customFailedCodes,
              };
            } else {
              return event;
            }
          }),
        };
        state.isInspectionUpdateSaved = true;
        checkReportValid(state);
      }
    },

    setInspectionQuestionnaireStart(state: AgencyReportsState) {
      if (state.currentReport) {
        state.isInspectionQuestionnaireLoadingError = [];
        state.isInspectionQuestionnaireLoading = true;
        state.isInspectionQuestionnaireSaved = false;
      }
    },
    setInspectionQuestionnaireFailed(state: AgencyReportsState, action: PayloadAction<string[]>) {
      if (state.currentReport) {
        state.isInspectionQuestionnaireLoading = false;
        state.isInspectionQuestionnaireLoadingError = action.payload;
        state.isInspectionQuestionnaireSaved = false;
      }
    },
    setInspectionQuestionnaireSuccess(
      state: AgencyReportsState,
      action: PayloadAction<Pick<AppNS.JobEventReport, 'inspectionQuestionnaire' | 'eventInspections'>>
    ) {
      if (state.currentReport) {
        state.isInspectionQuestionnaireLoading = false;
        state.isInspectionQuestionnaireLoadingError = [];
        state.currentReport = {
          ...state.currentReport,
          inspectionQuestionnaire: action.payload.inspectionQuestionnaire,
          ...(action.payload.eventInspections && {
            eventInspections: state.currentReport.eventInspections.map((event) => {
              const updatedInspection = action.payload.eventInspections.find((uI) => uI.id === event.id);

              if (updatedInspection) {
                return {
                  ...event,
                  status: (updatedInspection.state as AppNS.InspectionStatus) || 'failed',
                  notes: updatedInspection.notes,
                  failedCodes: updatedInspection.failedCodes,
                };
              }

              return event;
            }),
          }),
        };
        state.isInspectionQuestionnaireSaved = true;
      }
      checkReportValid(state);
    },

    setInspectionChecklistStart(state: AgencyReportsState) {
      if (state.currentReport) {
        state.isInspectionChecklistLoadingError = [];
        state.isInspectionChecklistLoading = true;
        state.isInspectionChecklistSaved = false;
      }
    },
    setInspectionChecklistFailed(state: AgencyReportsState, action: PayloadAction<string[]>) {
      if (state.currentReport) {
        state.isInspectionChecklistLoading = false;
        state.isInspectionChecklistLoadingError = action.payload;
        state.isInspectionChecklistSaved = false;
      }
    },
    setInspectionChecklistSuccess(
      state: AgencyReportsState,
      action: PayloadAction<Pick<AppNS.JobEventReport, 'inspectionChecklist'>>
    ) {
      if (state.currentReport) {
        state.isInspectionChecklistLoading = false;
        state.isInspectionChecklistLoadingError = [];
        state.currentReport = {
          ...state.currentReport,
          inspectionChecklist: action.payload.inspectionChecklist,
        };
        state.isInspectionChecklistSaved = true;
      }
      checkReportValid(state);
    },

    setTimeStart(state: AgencyReportsState) {
      state.isTimeLoading = true;
      state.isTimeLoadingError = [];
      state.isTimeSaved = false;
    },

    setTimeSuccess(
      state: AgencyReportsState,
      action: PayloadAction<{
        date: string;
        timeFrom: string;
        timeTo: string;
      }>
    ) {
      state.isTimeLoading = false;
      state.isTimeLoadingError = [];
      if (state.currentReport) {
        state.currentReport = {
          ...state.currentReport,
          timeFrom: action.payload.timeFrom,
          timeTo: action.payload.timeTo,
          date: action.payload.date,
        };
        state.isTimeSaved = true;
        checkReportValid(state);
      }
    },
    setTimeFailed(state: AgencyReportsState, action: PayloadAction<string[]>) {
      state.isTimeLoading = false;
      state.isTimeSaved = false;
      state.isTimeLoadingError = action.payload;
    },
    addFile(state: AgencyReportsState, action: PayloadAction<{ inspectionId: string; file: FileInfo }>) {
      if (state.currentReport) {
        state.currentReport.eventInspections = state.currentReport.eventInspections.map(
          (inspection: AppNS.Inspection) => {
            if (inspection.id === action.payload.inspectionId) {
              return {
                ...inspection,
                files: [...(inspection.files || []), action.payload.file],
              };
            } else {
              return inspection;
            }
          }
        );
      }
      checkReportValid(state);
    },
    updateFiles(state: AgencyReportsState, action: PayloadAction<{ inspectionId: string; files: FileInfo[] }>) {
      if (state.currentReport) {
        state.currentReport.eventInspections = state.currentReport.eventInspections.map(
          (inspection: AppNS.Inspection) => {
            if (inspection.id === action.payload.inspectionId) {
              return {
                ...inspection,
                files: action.payload.files,
              };
            } else {
              return inspection;
            }
          }
        );
      }
      checkReportValid(state);
    },
    removeImageStart(state: AgencyReportsState, action: PayloadAction<{ inspectionId: string; imageId: string }>) {
      if (state.currentReport) {
        state.currentReport.eventInspections = setEventInspectionFileStatus(
          state.currentReport.eventInspections,
          action.payload.inspectionId,
          action.payload.imageId,
          { isLoaded: false }
        );
      }
    },
    removeImageSuccess(state: AgencyReportsState, action: PayloadAction<{ inspectionId: string; imageId: string }>) {
      if (state.currentReport) {
        state.currentReport.eventInspections = setEventInspectionFileStatus(
          state.currentReport.eventInspections,
          action.payload.inspectionId,
          action.payload.imageId,
          { isRemoved: true }
        );
        checkReportValid(state);
      }
    },
    setFinalizeStart(state: AgencyReportsState) {
      state.isReportFinalizing = true;
      state.isReportFinalizingError = null;
    },
    setSubmitSuccess(state: AgencyReportsState, action: PayloadAction<AppNS.Params>) {
      state.isReportFinalizing = false;
      state.isReportFinalizingError = null;
      state.isReportValidChecked = false;
      state.reportValidErrors = [];
      if (state.currentReport) {
        state.currentReport = {
          ...state.currentReport,
          ...action.payload.properties,
        };
      }
    },
    setFinalizeFailed(state: AgencyReportsState, action: PayloadAction<AppNS.Params>) {
      state.isReportFinalizing = false;
      state.isReportFinalizingError = action.payload;
    },
    updateCostStart(state: AgencyReportsState) {
      state.isReportCostUpdating = true;
      state.isReportCostUpdated = false;
      state.isReportCostUpdatingError = null;
    },
    updateCostSuccess(state: AgencyReportsState, action: PayloadAction<AppNS.Params>) {
      state.isReportCostUpdating = false;
      state.isReportCostUpdated = true;
      state.isReportCostUpdatingError = null;
      state.currentReport = {
        ...state.currentReport,
        ...action.payload.properties,
      };
      checkReportValid(state);
    },
    updateCostFailed(state: AgencyReportsState, action: PayloadAction<AppNS.Params>) {
      state.isReportCostUpdating = false;
      state.isReportCostUpdated = false;
      state.isReportCostUpdatingError = action.payload;
    },
    resetUpdatingResults(state: AgencyReportsState) {
      state.isInspectorSaved = false;
      state.isSavingInspector = false;
      state.isSavingInspectorError = false;
    },
    checkingStart(state: AgencyReportsState, action: PayloadAction<'check_in' | 'check_out'>) {
      state.checkingType = action.payload;
      state.isCheckingIn = true;
      state.checkingInError = null;
    },
    checkingSuccess(state: AgencyReportsState, action: PayloadAction<AppNS.Params>) {
      state.checkingInError = null;
      state.isCheckingIn = false;
      if (state.currentReport) {
        if (action.payload.properties.type === 'check_in') {
          state.currentReport.checkIn = action.payload.properties;
          state.currentReport.state = 'in_progress';
        } else if (action.payload.properties.type === 'check_out') {
          state.currentReport.checkOut = action.payload.properties;
        }
      }
      checkReportValid(state);
    },
    checkingFailure(state: AgencyReportsState, action: PayloadAction<AppNS.Params>) {
      state.isCheckingIn = false;
      state.checkingInError = action.payload;
    },

    setDocument(state: AgencyReportsState, action: PayloadAction<AppNS.FileData>) {
      if (state.currentReport) {
        // if document exists
        const existingDocument = (state.currentReport?.documents || []).find(
          (document) => document.id === action.payload.id
        );

        if (existingDocument) {
          state.currentReport = {
            ...state.currentReport,
            documents: (state.currentReport?.documents || []).map((document) =>
              document.id === action.payload.id ? action.payload : document
            ),
          };
        } else {
          state.currentReport = {
            ...state.currentReport,
            documents: [...(state.currentReport.documents || []), action.payload],
          };
        }
        checkReportValid(state);
      }
    },

    setInspectionSummary(state: AgencyReportsState, action: PayloadAction<string>) {
      if (state.currentReport) {
        state.currentReport.notes = action.payload;
        checkReportValid(state);
      }
    },
  },
});

export const {
  addFile,
  getCurrentReportSuccess,
  getReportFailed,
  getReportStart,
  removeImageStart,
  removeImageSuccess,
  setDetailsActionDone,
  setDetailsPageTouched,
  setFinalizeFailed,
  setFinalizeStart,
  setSubmitSuccess,
  setIsReportValidChecked,
  setInspectionState,
  setReportStateFailed,
  setReportStateStart,
  setReportStateSuccess,
  setTimeFailed,
  setTimeSuccess,
  setTimeStart,
  setInspectionQuestionnaireFailed,
  setInspectionQuestionnaireSuccess,
  setInspectionQuestionnaireStart,
  setInspectionChecklistFailed,
  setInspectionChecklistSuccess,
  setInspectionChecklistStart,
  setInspector,
  setNotesSuccess,
  setNotesStart,
  setNotesFailed,
  setInspectionUpdateSuccess,
  setInspectionUpdateStart,
  setInspectionUpdateFailed,
  saveInspectorStart,
  saveInspectorSuccess,
  saveInspectorFailed,
  updateCostStart,
  updateCostSuccess,
  updateCostFailed,
  updateFiles,
  resetUpdatingResults,
  checkingStart,
  checkingSuccess,
  checkingFailure,

  setInspectionSummary,
  setDocument,
} = agencyReports.actions;

export default agencyReports.reducer;

export const getReport =
  (id: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getReportStart());
      const result = await apiGetReport(id);

      dispatch(getCurrentReportSuccess(result));
    } catch (err: any) {
      if (err instanceof axios.Cancel) {
        // when request is cancelled there is no need to throw error.
        return;
      }
      console.error(err.toString());
      dispatch(getReportFailed());
    }
  };

export const addReportDocument =
  (inspectionId: string, reportId: string, imageInfo: FileInfo): AppThunk =>
  async (dispatch) => {
    try {
      const output = await apiAddReportDocument({
        properties: {
          type: 'event_inspection',
          eventInspectionId: inspectionId,
          eventReportId: reportId,
          file: imageInfo.url || '',
        },
      });
      dispatch(addFile({ inspectionId, file: { ...output, uppyId: imageInfo.uppyId } }));
    } catch (err: any) {
      if (err instanceof axios.Cancel) {
        // when request is cancelled there is no need to throw error.
        return;
      }
      console.error(err.toString());
    }
  };

export const removeReportDocument =
  (inspectionId: string, imageId: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(removeImageStart({ inspectionId, imageId }));
      await apiDeleteReportDocument(imageId);
      dispatch(removeImageSuccess({ inspectionId, imageId }));
    } catch (err: any) {
      if (err instanceof axios.Cancel) {
        // when request is cancelled there is no need to throw error.
        return;
      }
      console.error(err.toString());
    }
  };

export const submitReport =
  (report: AppNS.JobEventReport): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setFinalizeStart());
      const result = await apiSubmitReport(report);
      dispatch(setSubmitSuccess(result));
      dispatch(setDetailsActionDone(true));
    } catch (err: any) {
      if (err instanceof axios.Cancel) {
        // when request is cancelled there is no need to throw error.
        return;
      }
      dispatch(setFinalizeFailed(err as AppNS.Params));
    }
  };

export const saveReportInspector =
  (reportId: string, properties: { inspectorId?: string; picInspectorId?: string }): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(saveInspectorStart());
      const result = await apiSaveReportInspector(reportId, properties);
      dispatch(saveInspectorSuccess(result));
    } catch (err: any) {
      if (err instanceof axios.Cancel) {
        // when request is cancelled there is no need to throw error.
        return;
      }
      console.error(err.toString());
      dispatch(saveInspectorFailed());
    }
  };

export const saveReportTime =
  (id: string, date: string, timeFrom: string, timeTo: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setTimeStart());
      await apiSaveReportTime(id, date, timeFrom, timeTo);
      dispatch(setTimeSuccess({ date, timeFrom, timeTo }));
    } catch (err: any) {
      dispatch(setTimeFailed(err as string[]));
    }
  };

export const saveReportNotes =
  (id: string, notes: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setNotesStart());
      await apiSaveReportNotes(id, notes);
      dispatch(setNotesSuccess(notes));
    } catch (err: any) {
      dispatch(setNotesFailed(err as string[]));
    }
  };

export const updateReportInspection =
  (
    id: string,
    notes: string,
    state: AppNS.InspectionStatus,
    failedCodes: string[],
    customFailedCodes: string
  ): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setInspectionUpdateStart(id));
      await apiUpdateReportInspection(id, notes, state, failedCodes, customFailedCodes);
      dispatch(
        setInspectionUpdateSuccess({
          id,
          notes,
          state,
          failedCodes,
          customFailedCodes,
        })
      );
    } catch (err: any) {
      dispatch(setInspectionUpdateFailed(err as string[]));
    }
  };

export const saveInspectionQuestionnaire =
  (id: string, docs: AppNS.Params): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setInspectionQuestionnaireStart());
      const docsUpdated = await apiSaveReportQuestionnaire(id, docs);
      dispatch(setInspectionQuestionnaireSuccess(docsUpdated));
    } catch (err: any) {
      dispatch(setInspectionQuestionnaireFailed(err as string[]));
    }
  };

export const saveInspectionChecklist =
  (id: string, inspectionChecklist: AppNS.Params): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(setInspectionChecklistStart());
      const docsUpdated = await apiSaveReportChecklist(id, inspectionChecklist);
      dispatch(setInspectionChecklistSuccess(docsUpdated));
    } catch (err: any) {
      dispatch(setInspectionChecklistFailed(err as string[]));
    }
  };

export const updateReportTotalCost =
  (id: string, cost: number): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(updateCostStart());
      const result = await apiUpdateReportTotalCost(id, cost);
      dispatch(updateCostSuccess(result));
    } catch (err: any) {
      if (err instanceof axios.Cancel) {
        // when request is cancelled there is no need to throw error.
        return;
      }
      console.error(err.toString());
      dispatch(updateCostFailed(err as AppNS.Params));
    }
  };

export const createReportChecking =
  (params: {
    eventReportId: string;
    lat: string;
    lng: string;
    unsafeConditions?: boolean;
    unsafeConditionsComment?: string;
    localizationError?: string;
    type: 'check_in' | 'check_out';
  }): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(checkingStart(params.type));
      const response = await createChecking(params);
      dispatch(checkingSuccess(response));
    } catch (err: any) {
      if (err) {
        dispatch(checkingFailure(err as AppNS.Params));
      }
    }
  };
